import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Check, Star, Award, Loader2, Crown } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const SPARKLE_OVERLAY = "https://static.prod-images.emergentagent.com/jobs/2c122ba0-c4f3-4497-9a5a-0002cdd6e57f/images/4c1845e26a2fcf5b3f9e3f9cca4d1fa8fc996f549630b0ad333e561292a49bc5.png";

const PLAN_VISUALS = {
  free: { icon: Sparkles, accent: "#7A8A81" },
  sparkle: { icon: Star, accent: "#0B5D3B" },
  crystal: { icon: Crown, accent: "#D4AF37" },
};

export default function MembershipPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [joining, setJoining] = useState(null);

  useEffect(() => {
    api.get("/membership/plans").then(r => setPlans(r.data.plans || [])).catch(() => {});
    if (user) api.get("/membership/me").then(r => setCurrent(r.data.plan?.id)).catch(() => {});
  }, [user]);

  const join = async (planId) => {
    if (!user) { nav("/login?next=/membership"); return; }
    setJoining(planId);
    try {
      await api.post("/membership/join", { plan_id: planId });
      setCurrent(planId);
      toast.success("Membership activated!", { description: "Your discount applies on every recurring booking." });
      if (planId !== "free") nav("/booking");
    } catch { toast.error("Could not activate"); }
    finally { setJoining(null); }
  };

  return (
    <div data-testid="membership-page">
      {/* Hero */}
      <section className="relative bg-[#111111] text-white overflow-hidden">
        <img src={SPARKLE_OVERLAY} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#063A24]/85 via-[#111]/85 to-[#0B5D3B]/40" />
        <div className="relative container-x py-24 md:py-32">
          <div className="overline mb-4 text-[#D4AF37]">ShoNics Memberships</div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight max-w-3xl">
            Cleaner home, every week —<br />
            <span className="gold-shimmer-text">on autopilot.</span>
          </h1>
          <p className="mt-6 text-white/80 text-lg max-w-xl">
            Lock in a recurring schedule, save up to 15% on every visit, and get the same trusted cleaner every time. No monthly fee. Cancel any time.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="container-x py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p) => {
            const v = PLAN_VISUALS[p.id] || PLAN_VISUALS.free;
            const Icon = v.icon;
            const isCurrent = current === p.id;
            return (
              <div
                key={p.id}
                className={`relative rounded-3xl p-8 md:p-10 flex flex-col ${
                  p.is_popular
                    ? "bg-[#0B5D3B] text-white shadow-[0_30px_80px_rgba(11,93,59,0.25)] md:scale-105 z-10"
                    : "bg-white border border-[#EAE3D2] shadow-[0_4px_24px_rgba(11,93,59,0.04)]"
                }`}
                data-testid={`plan-${p.id}`}
              >
                {p.is_popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#D4AF37] text-[#0B5D3B] text-[10px] uppercase tracking-[0.24em] font-bold">
                    Most Popular
                  </div>
                )}
                <Icon className={`w-7 h-7 mb-5 ${p.is_popular ? "text-[#D4AF37]" : ""}`} style={!p.is_popular ? { color: v.accent } : {}} strokeWidth={1.6} />
                <h3 className={`font-serif text-3xl ${p.is_popular ? "text-white" : "text-[#111]"}`}>{p.name}</h3>
                <p className={`text-sm mt-2 mb-7 ${p.is_popular ? "text-white/80" : "text-[#7A8A81]"}`}>{p.tagline}</p>

                <div className="flex items-baseline gap-1 mb-7">
                  <span className={`font-serif text-5xl ${p.is_popular ? "text-[#D4AF37]" : "text-[#0B5D3B]"}`}>
                    {p.discount_percent > 0 ? `${p.discount_percent}%` : "$0"}
                  </span>
                  <span className={`text-sm ${p.is_popular ? "text-white/70" : "text-[#7A8A81]"}`}>
                    {p.discount_percent > 0 ? "off every clean" : "/month · no fee"}
                  </span>
                </div>

                <ul className="space-y-3 mb-9 flex-1">
                  {p.perks.map((perk, i) => (
                    <li key={i} className={`flex items-start gap-3 text-sm ${p.is_popular ? "text-white/90" : "text-[#4A554E]"}`}>
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.is_popular ? "text-[#D4AF37]" : "text-[#0B5D3B]"}`} />
                      {perk}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => join(p.id)}
                  disabled={joining === p.id || isCurrent}
                  className={`w-full px-6 py-3.5 rounded-full font-semibold tracking-wide transition-colors disabled:opacity-60 ${
                    p.is_popular
                      ? "bg-[#D4AF37] text-[#0B5D3B] hover:bg-white"
                      : "bg-[#0B5D3B] text-white hover:bg-[#063A24]"
                  }`}
                  data-testid={`join-${p.id}`}
                >
                  {isCurrent ? "Your current plan" : joining === p.id ? <><Loader2 className="w-4 h-4 mr-2 animate-spin inline" /> Activating…</> : (p.id === "free" ? "Continue free" : "Become a member")}
                </button>
              </div>
            );
          })}
        </div>

        {/* Trust strip */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { label: "No monthly fee", icon: Award },
            { label: "Cancel any time", icon: Check },
            { label: "Same cleaner each visit", icon: Star },
            { label: "Free reschedule 24h", icon: Sparkles },
          ].map((b, i) => {
            const Ic = b.icon;
            return (
              <div key={i} className="card-luxe p-6">
                <Ic className="w-5 h-5 text-[#D4AF37] mx-auto mb-3" strokeWidth={1.6} />
                <div className="text-sm font-medium text-[#111]">{b.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="container-x pb-24">
        <div className="card-luxe p-12 md:p-16 text-center bg-gradient-to-br from-[#063A24] via-[#0B5D3B] to-[#063A24] text-white relative overflow-hidden">
          <img src={SPARKLE_OVERLAY} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none" />
          <div className="relative">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Not sure which plan fits?</h2>
            <p className="mt-4 text-white/80 max-w-xl mx-auto">Talk to Sparkle — our AI concierge — or grab a free quote in 60 seconds.</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/ai-quote" className="btn-gold">Try Free AI Quote</Link>
              <Link to="/contact" className="bg-white/10 backdrop-blur-md text-white border border-white/20 px-7 py-3.5 rounded-full font-medium hover:bg-white/20 transition-all">Talk to us</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
